import {AppleIcon} from '@github-ui/custom-icons/AppleIcon'
import {LinuxIcon} from '@github-ui/custom-icons/LinuxIcon'
import {WindowsIcon} from '@github-ui/custom-icons/WindowsIcon'
import {announce} from '@github-ui/aria-live'
import {getOS, OS, type OS as OperatingSystem} from '@github-ui/get-os'
import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import useColorModes from '@github-ui/react-core/use-color-modes'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {Icon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import type {CSSProperties} from 'react'
import {useState, useSyncExternalStore} from 'react'

import backgroundDark from './assets/background-dark.png'
import backgroundLight from './assets/background-light.png'
import bannerDark from './assets/banner-dark.png'
import bannerLight from './assets/banner-light.png'
import styles from './CopilotAppBanner.module.css'
import {
  PromotionalBanner,
  type PromotionalBannerAction,
  type PromotionalBannerPayload,
} from '../promotional-banner/PromotionalBanner'

export type CopilotAppDownloadPlatform = 'mac' | 'windows' | 'linux'

export interface CopilotAppBannerProps {
  banner: CopilotAppBannerPayload
  rounded?: boolean
  downloadPlatform?: CopilotAppDownloadPlatform
  operatingSystem?: OperatingSystem
}

interface SendDownloadLinkAction {
  label: string
  endpoint: string
  analyticsAction: string
}

export interface CopilotAppBannerPayload extends PromotionalBannerPayload {
  sendDownloadLinkAction?: SendDownloadLinkAction
}

const ICON_BY_PLATFORM: Record<CopilotAppDownloadPlatform, Icon> = {
  mac: AppleIcon,
  windows: WindowsIcon,
  linux: LinuxIcon,
}

const DOWNLOAD_LABEL_BY_PLATFORM: Record<CopilotAppDownloadPlatform, string> = {
  mac: 'Download for macOS',
  windows: 'Download for Windows',
  linux: 'Download for Linux',
}

// Keyed on the campaign's action ids rather than `variant`: `variant` is the Datadog tag, and
// renaming one for reporting should not silently change which download the CTA offers.
const PLATFORM_BY_ACTION_ID: Record<string, CopilotAppDownloadPlatform> = {
  'download-mac-silicon': 'mac',
  'download-mac-intel': 'mac',
  'download-win-x64': 'windows',
  'download-win-arm': 'windows',
  'download-linux': 'linux',
}

const DEFAULT_DOWNLOAD_PLATFORM: CopilotAppDownloadPlatform = 'mac'
const TELEMETRY = {component: 'CopilotAppBanner', variant: 'download'}
const ARTWORK_BY_THEME_FAMILY = {
  light: {desktop: bannerLight, compact: backgroundLight},
  dark: {desktop: bannerDark, compact: backgroundDark},
} as const

type ThemeFamily = keyof typeof ARTWORK_BY_THEME_FAMILY

function getArtworkForScheme(scheme: string | undefined, fallback: ThemeFamily) {
  const family = scheme ? (scheme.startsWith('dark') ? 'dark' : 'light') : fallback
  return ARTWORK_BY_THEME_FAMILY[family]
}

function subscribeToDownloadPlatform() {
  return () => {}
}

export function resolveDownloadPlatform(os: OperatingSystem): CopilotAppDownloadPlatform {
  switch (os) {
    case OS.Windows:
      return 'windows'
    case OS.Linux:
      return 'linux'
    case OS.macOS:
    default:
      return DEFAULT_DOWNLOAD_PLATFORM
  }
}

function getBrowserDownloadPlatform(): CopilotAppDownloadPlatform {
  return resolveDownloadPlatform(getOS().os)
}

function getServerDownloadPlatform(): CopilotAppDownloadPlatform {
  return DEFAULT_DOWNLOAD_PLATFORM
}

function useDetectedDownloadPlatform() {
  return useSyncExternalStore(subscribeToDownloadPlatform, getBrowserDownloadPlatform, getServerDownloadPlatform)
}

function useDetectedOperatingSystem() {
  return useSyncExternalStore(
    subscribeToDownloadPlatform,
    () => getOS().os,
    () => OS.Unknown,
  )
}

function platformOf(action: PromotionalBannerAction): CopilotAppDownloadPlatform | undefined {
  return PLATFORM_BY_ACTION_ID[action.id]
}

export function CopilotAppBanner({banner, rounded = true, downloadPlatform, operatingSystem}: CopilotAppBannerProps) {
  const {dayScheme, nightScheme} = useColorModes()
  const detectedDownloadPlatform = useDetectedDownloadPlatform()
  const detectedOperatingSystem = useDetectedOperatingSystem()
  const resolvedOperatingSystem = operatingSystem ?? detectedOperatingSystem
  const resolvedPlatform = downloadPlatform ?? detectedDownloadPlatform
  const [sendState, setSendState] = useState<'idle' | 'pending' | 'sent' | 'error'>('idle')
  const download = banner.actionMenu?.items.find(item => platformOf(item) === resolvedPlatform)
  const dayArtwork = getArtworkForScheme(dayScheme, 'light')
  const nightArtwork = getArtworkForScheme(nightScheme, 'dark')
  const artworkStyle = {
    '--copilot-app-banner-day': `url(${dayArtwork.desktop})`,
    '--copilot-app-banner-night': `url(${nightArtwork.desktop})`,
    '--copilot-app-banner-day-compact': `url(${dayArtwork.compact})`,
    '--copilot-app-banner-night-compact': `url(${nightArtwork.compact})`,
  } as CSSProperties
  // No variant here, so platform-specific download clicks keep the banner's `download` variant.
  const primaryAction = download
    ? {
        id: download.id,
        label: DOWNLOAD_LABEL_BY_PLATFORM[resolvedPlatform],
        href: download.href,
        analyticsAction: download.analyticsAction,
        openInNewTab: download.openInNewTab,
      }
    : banner.primaryAction
  const sendDownloadLinkAction = banner.sendDownloadLinkAction
  const showSendDownloadLink =
    sendDownloadLinkAction && (resolvedOperatingSystem === OS.iOS || resolvedOperatingSystem === OS.Android)

  const handleSendDownloadLink = async (telemetryTags: IpmMetricTags) => {
    if (!sendDownloadLinkAction || sendState === 'pending' || sendState === 'sent') return

    reportIpmAction('cta_click', telemetryTags)
    setSendState('pending')
    try {
      const response = await reactFetchJSON(sendDownloadLinkAction.endpoint, {method: 'POST', body: {}})
      const result = (await response.json()) as {status?: string}
      if (!response.ok || result.status !== 'sent') {
        throw new Error('Download link email was not sent')
      }

      setSendState('sent')
      announce('Download link sent.')
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      setSendState('error')
      announce('Something went wrong. Try again.', {assertive: true})
    }
  }

  const customPrimaryAction = showSendDownloadLink
    ? (telemetryTags: IpmMetricTags) => (
        <div>
          <Button
            onClick={() => void handleSendDownloadLink(telemetryTags)}
            loading={sendState === 'pending'}
            loadingAnnouncement="Sending download link"
            inactive={sendState === 'sent' || undefined}
            aria-disabled={sendState === 'pending' || sendState === 'sent' || undefined}
            data-analytics-event={JSON.stringify({
              category: banner.analytics.category,
              action: sendDownloadLinkAction.analyticsAction,
              label: `${banner.analytics.label}location:${banner.analytics.location};`,
            })}
          >
            {sendState === 'pending'
              ? 'Sending...'
              : sendState === 'sent'
                ? 'Download link sent'
                : sendDownloadLinkAction.label}
          </Button>
          {sendState === 'error' ? <span className="ml-2">Something went wrong. Try again.</span> : null}
        </div>
      )
    : undefined

  return (
    <PromotionalBanner
      banner={{...banner, primaryAction}}
      rounded={rounded}
      customPrimaryAction={customPrimaryAction}
      surfaceClassName={styles.Surface}
      surfaceStyle={artworkStyle}
      actionVisual={action => {
        const platform = platformOf(action)
        if (!platform) return null
        const PlatformIcon = ICON_BY_PLATFORM[platform]
        return <PlatformIcon />
      }}
      telemetry={TELEMETRY}
    />
  )
}
